import { Button, Flex, Text } from '@mantine/core';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { resetKycForm } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { DataKYCFileTctWithFile, GetKYCInvestorInstanceRes } from '@dealstack/shared/lib/generated';
import {
	Step,
	StepType,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/types';
import { useManageKYCController } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/useManageKYCController';
import { useDeleteKycFile } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/api/useDeleteKycFile';
import { AddKycFileButton } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/Review/component/AddKycFileButton';
import { ReplaceKycFileButton } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/Review/component/ReplaceKycFileButton';
import { ConfirmDeleteDrawer } from '@dealstack/frontend/shared/components/ConfirmDeleteDrawer/ConfirmDeleteDrawer';
import {
	SelectedTemplate,
	isFormSubmitted,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/FormsCategoryList/types';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { formatDateToUI, DATE_FORMATS } from '@dealstack/shared/lib/format/dates/date';
import { RequestAdditionalDocumentDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/Files/RequestAdditionalDocumentDrawer';
import { useUpdateInvestorProcessSettings } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/api/useUpdateInvestorProcessSettings';
import {
	FileInfoCard,
	InfoRow,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/StepContent/component/RightPanel/component/Files/styled';

interface Props {
	currentInvestor: GetKYCInvestorInstanceRes | undefined;
	currentStep: Step;
	selectedKycFile: DataKYCFileTctWithFile | undefined;
	filesForStep: DataKYCFileTctWithFile[];
	selectedTemplate?: SelectedTemplate;
	isReadOnly?: boolean;
}

export const FilesTab = ({
	currentInvestor,
	currentStep,
	selectedKycFile,
	filesForStep,
	selectedTemplate,
	isReadOnly = false,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const { dealId, mipId } = useRequiredParams('dealId', 'mipId');
	const queryClient = useQueryClient();
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [showFormDeleteModal, setShowFormDeleteModal] = useState(false);
	const [showRequestDrawer, setShowRequestDrawer] = useState(false);

	const [searchParams] = useSearchParams();
	const kycWorkflowId = searchParams.get('kycWorkflowId') ?? undefined;
	const { getKYCDataRequest } = useManageKYCController(currentInvestor?.id, kycWorkflowId);
	const { deleteKycFile, mutation: deleteKycFileMutation } = useDeleteKycFile(getKYCDataRequest);

	// Forms are DocumentWorkflow-backed: deleting a submitted form resets its instance + bridge
	// (server-side recompute), rather than deleting a KYCFile.
	const resetFormMutation = useMutation({
		mutationFn: (templateId: string) =>
			resetKycForm({
				dealId,
				programmeId: mipId,
				kycWorkflowId: kycWorkflowId ?? '',
				templateId,
			}),
		onSuccess: () => {
			setShowFormDeleteModal(false);
			return Promise.all([
				queryClient.invalidateQueries({
					queryKey: [
						API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_KYC_FORMS_KYC_WORKFLOW_KYCWORKFLOWID_INVESTOR_FORMS_GET,
					],
				}),
				queryClient.invalidateQueries({
					queryKey: [
						API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_KYC_FORMS_KYC_WORKFLOW_KYCWORKFLOWID_GET,
					],
				}),
				getKYCDataRequest.refetch(),
			]);
		},
	});

	const isFormStep = currentStep?.type === StepType.FORM;
	const isPivStep = currentStep?.type === StepType.INVESTMENT_VEHICLE;

	const allowedToAddAnotherFile =
		currentStep?.allowFileUpload &&
		currentStep?.allowMultipleFileUpload &&
		!isFormStep &&
		!isPivStep;

	const handleDeleteKycFile = () => {
		deleteKycFile({
			fileId: selectedKycFile?.id ?? '',
			userId: currentInvestor?.user.id ?? '',
		});
		setShowDeleteModal(false);
	};

	const areMorePivDocsRequested = currentInvestor?.processSettings?.areMorePivDocsRequested;

	const { handleRequestMorePivDocs, updateInvestorProcessSettingsRequest } =
		useUpdateInvestorProcessSettings({
			activeInvestorId: currentInvestor?.id,
			kycWorkflowId,
		});

	if (isReadOnly) {
		return null;
	}

	if (isFormStep && selectedTemplate) {
		// Unsubmitted form → upload (writes the DocumentWorkflow instance/bridge via templateId).
		if (!isFormSubmitted(selectedTemplate)) {
			return (
				<Flex direction="column" gap="1rem">
					<AddKycFileButton
						currentStep={currentStep}
						currentInvestor={currentInvestor}
						templateIdOverride={selectedTemplate.templateId}
					/>
				</Flex>
			);
		}
		// Submitted form → show the file + a delete that resets the DocumentWorkflow instance/bridge.
		return (
			<Flex direction="column" gap="1rem">
				<FileInfoCard>
					<InfoRow>
						<Text size="B5" c="dark.0">
							{tMipManage('kyc.files.filename')}
						</Text>
						<Text size="B5Bold">{selectedTemplate.fileName ?? '—'}</Text>
					</InfoRow>
					<InfoRow>
						<Text size="B5" c="dark.0">
							{tMipManage('kyc.files.uploaded')}
						</Text>
						<Text size="B5Bold">
							{selectedTemplate.createdAt
								? formatDateToUI(
										new Date(selectedTemplate.createdAt),
										DATE_FORMATS.DATE_TIME_ALT
									)
								: '—'}
						</Text>
					</InfoRow>
					<Flex justify="center" mt="0.5rem">
						<Button
							variant="subtle"
							color="red"
							onClick={() => setShowFormDeleteModal(true)}
						>
							{tMipManage('kyc.review.delete_file')}
						</Button>
					</Flex>
				</FileInfoCard>
				<ConfirmDeleteDrawer
					opened={showFormDeleteModal}
					onClose={() => setShowFormDeleteModal(false)}
					title={tMipManage('kyc.delete_file_drawer.title')}
					confirmDeleteText={selectedTemplate.fileName ?? selectedTemplate.templateName}
					inputLabel={tMipManage('kyc.delete_file_drawer.input_label')}
					inputPlaceholder={tMipManage('kyc.delete_file_drawer.input_placeholder')}
					wordMismatchErrorMessage={tMipManage('kyc.delete_file_drawer.mismatch')}
					deleteBtnText={tMipManage('kyc.review.delete_file')}
					cancelBtnText={tMipManage('kyc.review.cancel')}
					handleDelete={() => resetFormMutation.mutate(selectedTemplate.templateId)}
					deleteLoading={resetFormMutation.isPending}
				/>
			</Flex>
		);
	}

	return (
		<Flex direction="column" gap="1rem">
			{selectedKycFile && (
				<FileInfoCard>
					<InfoRow>
						<Text size="B5" c="dark.0">
							{tMipManage('kyc.files.filename')}
						</Text>
						<Text size="B5Bold">{selectedKycFile.file?.name ?? '—'}</Text>
					</InfoRow>
					<InfoRow>
						<Text size="B5" c="dark.0">
							{tMipManage('kyc.files.uploaded')}
						</Text>
						<Text size="B5Bold">
							{selectedKycFile.createdAt
								? formatDateToUI(
										new Date(selectedKycFile.createdAt),
										DATE_FORMATS.DATE_TIME_ALT
									)
								: '—'}
						</Text>
					</InfoRow>
					<Flex justify="center" mt="0.5rem">
						<Button
							variant="subtle"
							color="red"
							onClick={() => setShowDeleteModal(true)}
						>
							{tMipManage('kyc.review.delete_file')}
						</Button>
					</Flex>
				</FileInfoCard>
			)}
			{selectedKycFile?.id && (
				<ReplaceKycFileButton
					selectedKycId={selectedKycFile.id}
					activeInvestorId={currentInvestor?.id}
				/>
			)}
			{allowedToAddAnotherFile && filesForStep.length > 0 && (
				<AddKycFileButton
					currentStep={currentStep}
					currentInvestor={currentInvestor}
					selectedKycFile={selectedKycFile}
				/>
			)}
			{isPivStep && (
				<Button
					variant="default"
					fullWidth
					leftSection={<Icon variant="add-document" size="xl" />}
					loading={updateInvestorProcessSettingsRequest.isPending}
					onClick={() => {
						if (areMorePivDocsRequested) {
							handleRequestMorePivDocs(false);
							return;
						}
						setShowRequestDrawer(true);
					}}
				>
					{areMorePivDocsRequested
						? tMipManage('kyc.review.cancel_additional_file_request')
						: tMipManage('kyc.review.request_additional_file')}
				</Button>
			)}
			<ConfirmDeleteDrawer
				opened={showDeleteModal}
				onClose={() => setShowDeleteModal(false)}
				title={tMipManage('kyc.delete_file_drawer.title')}
				confirmDeleteText={selectedKycFile?.file.name ?? ''}
				inputLabel={tMipManage('kyc.delete_file_drawer.input_label')}
				inputPlaceholder={tMipManage('kyc.delete_file_drawer.input_placeholder')}
				wordMismatchErrorMessage={tMipManage('kyc.delete_file_drawer.mismatch')}
				deleteBtnText={tMipManage('kyc.review.delete_file')}
				cancelBtnText={tMipManage('kyc.review.cancel')}
				handleDelete={handleDeleteKycFile}
				deleteLoading={deleteKycFileMutation.isPending}
			/>
			{isPivStep && (
				<RequestAdditionalDocumentDrawer
					opened={showRequestDrawer}
					onClose={() => setShowRequestDrawer(false)}
					activeInvestorId={currentInvestor?.id}
					kycWorkflowId={kycWorkflowId}
				/>
			)}
		</Flex>
	);
};
